/**
 * Scene System V2 - Scene Condition Engine
 *
 * Processes scene conditions using device capabilities from device configurations.
 * Independent engine for Flow-based scene condition evaluation.
 */

import type {
	SceneCondition,
	SceneExecutionContext,
	LegacySceneCondition,
	ParameterValue,
	ComparisonOperator,
	StateProperty,
} from '../types/scene-capabilities';
import { deviceSceneCapabilities } from './DeviceSceneCapabilities';

export interface ConditionEvaluationResult {
	success: boolean;
	result?: boolean;
	error?: string;
	warnings?: string[];
	metadata?: Record<string, any>;
}

export interface ConditionEvaluationOptions {
	validateOnly?: boolean;
	currentState?: Record<string, any>;
	historicalData?: Array<{ timestamp: number; state: Record<string, any> }>;
}

export class SceneConditionEngine {
	private static instance: SceneConditionEngine;

	private constructor() {}

	public static getInstance(): SceneConditionEngine {
		if (!SceneConditionEngine.instance) {
			SceneConditionEngine.instance = new SceneConditionEngine();
		}
		return SceneConditionEngine.instance;
	}

	/**
	 * Evaluate a scene condition using device capabilities
	 */
	public async evaluateCondition(
		context: SceneExecutionContext,
		conditionName: string,
		parameters: Record<string, ParameterValue>,
		options: ConditionEvaluationOptions = {},
	): Promise<ConditionEvaluationResult> {
		try {
			// Get condition definition from device capabilities
			const conditionDef = context.capabilities.conditions[conditionName];

			if (!conditionDef) {
				return {
					success: false,
					error: `Condition '${conditionName}' not supported by device type '${context.deviceType}'`,
				};
			}

			// Validate parameters
			const validationResult = this.validateConditionParameters(conditionDef, parameters);
			if (!validationResult.success) {
				return {
					success: false,
					error: validationResult.error,
					warnings: validationResult.warnings,
				};
			}

			// Return early if validation only
			if (options.validateOnly) {
				return {
					success: true,
					metadata: { validated: true },
				};
			}

			// Evaluate the condition
			const evaluationResult = await this.performConditionEvaluation(
				context,
				conditionDef,
				parameters,
				options,
			);

			return {
				success: true,
				result: evaluationResult.result,
				warnings: evaluationResult.warnings,
				metadata: evaluationResult.metadata,
			};
		} catch (error) {
			return {
				success: false,
				error: `Condition evaluation failed: ${error.message}`,
			};
		}
	}

	/**
	 * Evaluate multiple conditions with logical operators
	 */
	public async evaluateConditions(
		context: SceneExecutionContext,
		conditions: Array<{
			name: string;
			parameters: Record<string, ParameterValue>;
			operator?: 'AND' | 'OR';
		}>,
		options: ConditionEvaluationOptions = {},
	): Promise<ConditionEvaluationResult> {
		const results: ConditionEvaluationResult[] = [];

		for (const condition of conditions) {
			const result = await this.evaluateCondition(
				context,
				condition.name,
				condition.parameters,
				options,
			);
			results.push(result);

			// Stop on first failure unless continuing is specified
			if (!result.success && !options.validateOnly) {
				return result;
			}
		}

		// Combine results with logical operators
		return this.combineConditionResults(conditions, results);
	}

	/**
	 * Convert legacy scene condition to V2 format and evaluate
	 */
	public async evaluateLegacyCondition(
		context: SceneExecutionContext,
		legacyCondition: LegacySceneCondition,
		options: ConditionEvaluationOptions = {},
	): Promise<ConditionEvaluationResult> {
		try {
			// Convert legacy condition to V2 format
			const v2Condition = this.convertLegacyCondition(legacyCondition);

			if (!v2Condition) {
				return {
					success: false,
					error: `Unable to convert legacy condition: ${legacyCondition.property}`,
				};
			}

			// Evaluate using V2 engine
			return await this.evaluateCondition(
				context,
				v2Condition.name,
				v2Condition.parameters,
				options,
			);
		} catch (error) {
			return {
				success: false,
				error: `Legacy condition evaluation failed: ${error.message}`,
			};
		}
	}

	/**
	 * Get available conditions for a device type
	 */
	public getAvailableConditions(deviceType: string): string[] {
		const capabilities = deviceSceneCapabilities.getCapabilities(deviceType);

		if (!capabilities) {
			return [];
		}

		return Object.keys(capabilities.conditions);
	}

	/**
	 * Get condition definition for a device type and condition
	 */
	public getConditionDefinition(deviceType: string, conditionName: string): SceneCondition | null {
		const capabilities = deviceSceneCapabilities.getCapabilities(deviceType);

		if (!capabilities) {
			return null;
		}

		return capabilities.conditions[conditionName] || null;
	}

	/**
	 * Get current device state for condition evaluation
	 */
	public async getDeviceState(
		deviceId: string,
		stateProperties: string[],
	): Promise<Record<string, any> | null> {
		try {
			// STUB: Device state retrieval not yet integrated with the live device communication system.
			// When implemented, this should query the device store or API for real-time state values.
			// Currently returns null, which means conditions using device state will not evaluate correctly.

			console.log(
				`SceneConditionEngine: Getting state for device ${deviceId}, properties:`,
				stateProperties,
			);

			// Placeholder return - in real implementation this would fetch actual device state
			return {};
		} catch (error) {
			console.error('SceneConditionEngine: Failed to get device state:', error);
			return null;
		}
	}

	// Private methods

	private validateConditionParameters(
		condition: SceneCondition,
		parameters: Record<string, ParameterValue>,
	): { success: boolean; error?: string; warnings?: string[] } {
		const warnings: string[] = [];

		// Check required parameters
		for (const [paramName, paramDef] of Object.entries(condition.parameters)) {
			if (paramDef.required && !(paramName in parameters)) {
				return {
					success: false,
					error: `Required parameter '${paramName}' is missing`,
				};
			}
		}

		// Validate parameter types and constraints
		for (const [paramName, value] of Object.entries(parameters)) {
			const paramDef = condition.parameters[paramName];

			if (!paramDef) {
				warnings.push(`Unknown parameter '${paramName}' will be ignored`);
				continue;
			}

			const typeValidation = this.validateParameterType(paramDef.type, value);
			if (!typeValidation.success) {
				return {
					success: false,
					error: `Parameter '${paramName}': ${typeValidation.error}`,
				};
			}

			const constraintValidation = this.validateParameterConstraints(paramDef, value);
			if (!constraintValidation.success) {
				return {
					success: false,
					error: `Parameter '${paramName}': ${constraintValidation.error}`,
				};
			}
		}

		return { success: true, warnings };
	}

	private async performConditionEvaluation(
		context: SceneExecutionContext,
		condition: SceneCondition,
		parameters: Record<string, ParameterValue>,
		options: ConditionEvaluationOptions,
	): Promise<{ result: boolean; warnings?: string[]; metadata?: Record<string, any> }> {
		const warnings: string[] = [];
		const metadata: Record<string, any> = {
			evaluationType: condition.evaluationType,
			evaluatedAt: Date.now(),
		};

		switch (condition.evaluationType) {
			case 'immediate':
				return await this.evaluateImmediateCondition(context, condition, parameters, options);

			case 'change':
				return await this.evaluateChangeCondition(context, condition, parameters, options);

			case 'duration':
				return await this.evaluateDurationCondition(context, condition, parameters, options);

			case 'threshold':
				return await this.evaluateThresholdCondition(context, condition, parameters, options);

			case 'schedule':
				return await this.evaluateScheduleCondition(context, condition, parameters, options);

			default:
				throw new Error(`Unsupported condition evaluation type: ${condition.evaluationType}`);
		}
	}

	private async evaluateImmediateCondition(
		context: SceneExecutionContext,
		condition: SceneCondition,
		parameters: Record<string, ParameterValue>,
		options: ConditionEvaluationOptions,
	): Promise<{ result: boolean; warnings?: string[]; metadata?: Record<string, any> }> {
		// Get current device state
		const deviceState = options.currentState || context.currentState;

		if (!deviceState) {
			throw new Error('Current device state is required for immediate condition evaluation');
		}

		// Extract comparison parameters
		const property = parameters.property as string;
		const operator = parameters.operator as ComparisonOperator;
		const expectedValue = parameters.value;

		if (!property || !operator || expectedValue === undefined) {
			throw new Error('Immediate condition requires property, operator, and value parameters');
		}

		// Get current property value
		const currentValue = this.getPropertyValue(deviceState, property);

		if (currentValue === undefined) {
			throw new Error(`Property '${property}' not found in device state`);
		}

		// Perform comparison
		const result = this.compareValues(currentValue, operator, expectedValue);

		return {
			result,
			metadata: {
				property,
				operator,
				currentValue,
				expectedValue,
				comparisonResult: result,
			},
		};
	}

	private async evaluateChangeCondition(
		context: SceneExecutionContext,
		condition: SceneCondition,
		parameters: Record<string, ParameterValue>,
		options: ConditionEvaluationOptions,
	): Promise<{ result: boolean; warnings?: string[]; metadata?: Record<string, any> }> {
		// For change conditions, we need historical data
		const historicalData = options.historicalData;

		if (!historicalData || historicalData.length < 2) {
			return {
				result: false,
				metadata: { reason: 'Insufficient historical data for change detection' },
			};
		}

		const property = parameters.property as string;
		const changeType = (parameters.changeType as string) || 'any'; // 'any', 'increase', 'decrease'

		// Get previous and current values
		const previousState = historicalData[historicalData.length - 2].state;
		const currentState = historicalData[historicalData.length - 1].state;

		const previousValue = this.getPropertyValue(previousState, property);
		const currentValue = this.getPropertyValue(currentState, property);

		if (previousValue === undefined || currentValue === undefined) {
			throw new Error(`Property '${property}' not found in historical data`);
		}

		// Evaluate change based on type
		let result = false;

		switch (changeType) {
			case 'any':
				result = previousValue !== currentValue;
				break;
			case 'increase':
				result = currentValue > previousValue;
				break;
			case 'decrease':
				result = currentValue < previousValue;
				break;
			default:
				throw new Error(`Unsupported change type: ${changeType}`);
		}

		return {
			result,
			metadata: {
				property,
				changeType,
				previousValue,
				currentValue,
				changed: result,
			},
		};
	}

	private async evaluateDurationCondition(
		context: SceneExecutionContext,
		condition: SceneCondition,
		parameters: Record<string, ParameterValue>,
		options: ConditionEvaluationOptions,
	): Promise<{ result: boolean; warnings?: string[]; metadata?: Record<string, any> }> {
		// Duration conditions require time-based evaluation
		// This would typically involve checking if a condition has been true for a specified duration

		return {
			result: false,
			metadata: { reason: 'Duration condition evaluation not implemented yet' },
		};
	}

	private async evaluateThresholdCondition(
		context: SceneExecutionContext,
		condition: SceneCondition,
		parameters: Record<string, ParameterValue>,
		options: ConditionEvaluationOptions,
	): Promise<{ result: boolean; warnings?: string[]; metadata?: Record<string, any> }> {
		// Threshold conditions are similar to immediate but with hysteresis support
		const evaluationResult = await this.evaluateImmediateCondition(
			context,
			condition,
			parameters,
			options,
		);

		// STUB: Hysteresis logic not yet implemented.
		// When thresholdSupport config includes hysteresis, this should apply dead-band logic
		// to prevent rapid on/off toggling near threshold values.

		return evaluationResult;
	}

	private async evaluateScheduleCondition(
		context: SceneExecutionContext,
		condition: SceneCondition,
		parameters: Record<string, ParameterValue>,
		options: ConditionEvaluationOptions,
	): Promise<{ result: boolean; warnings?: string[]; metadata?: Record<string, any> }> {
		// Schedule conditions evaluate based on time/date criteria
		const currentTime = new Date();

		// STUB: Schedule condition evaluation not yet implemented.
		// Should handle time ranges (e.g., "between 8:00-17:00"), days of week,
		// specific dates, and recurring schedule patterns.

		return {
			result: false,
			metadata: { reason: 'Schedule condition evaluation not implemented yet' },
		};
	}

	private combineConditionResults(
		conditions: Array<{ operator?: 'AND' | 'OR' }>,
		results: ConditionEvaluationResult[],
	): ConditionEvaluationResult {
		if (results.length === 0) {
			return { success: true, result: true };
		}

		if (results.length === 1) {
			return results[0];
		}

		// Default to AND logic if no operators specified
		let combinedResult = results[0].result ?? true;
		const allWarnings: string[] = [];
		const allMetadata: Record<string, any> = {};

		for (let i = 1; i < results.length; i++) {
			const operator = conditions[i].operator || 'AND';
			const currentResult = results[i].result ?? false;

			if (operator === 'AND') {
				combinedResult = combinedResult && currentResult;
			} else if (operator === 'OR') {
				combinedResult = combinedResult || currentResult;
			}

			// Collect warnings and metadata
			if (results[i].warnings) {
				allWarnings.push(...results[i].warnings);
			}
			if (results[i].metadata) {
				Object.assign(allMetadata, results[i].metadata);
			}
		}

		return {
			success: true,
			result: combinedResult,
			warnings: allWarnings.length > 0 ? allWarnings : undefined,
			metadata: Object.keys(allMetadata).length > 0 ? allMetadata : undefined,
		};
	}

	private convertLegacyCondition(
		legacyCondition: LegacySceneCondition,
	): { name: string; parameters: Record<string, ParameterValue> } | null {
		// Map legacy condition properties to V2 condition names
		const propertyMap: Record<string, string> = {
			switch: 'is_on',
			state: 'is_on',
			brightness: 'brightness_level',
			temperature: 'temperature',
			power: 'power_consumption',
		};

		const v2ConditionName = propertyMap[legacyCondition.property];

		if (!v2ConditionName) {
			return null;
		}

		// Convert legacy parameters to V2 format
		const parameters: Record<string, ParameterValue> = {
			property: legacyCondition.property,
			operator: legacyCondition.operator,
			value: legacyCondition.value,
		};

		return {
			name: v2ConditionName,
			parameters,
		};
	}

	private validateParameterType(
		expectedType: string,
		value: ParameterValue,
	): { success: boolean; error?: string } {
		switch (expectedType) {
			case 'boolean':
				if (typeof value !== 'boolean') {
					return { success: false, error: 'Expected boolean value' };
				}
				break;

			case 'number':
				if (typeof value !== 'number' || isNaN(value)) {
					return { success: false, error: 'Expected numeric value' };
				}
				break;

			case 'string':
				if (typeof value !== 'string') {
					return { success: false, error: 'Expected string value' };
				}
				break;

			default:
				return { success: false, error: `Unknown parameter type: ${expectedType}` };
		}

		return { success: true };
	}

	private validateParameterConstraints(
		paramDef: any,
		value: ParameterValue,
	): { success: boolean; error?: string } {
		// Range validation
		if (paramDef.range && typeof value === 'number') {
			if (paramDef.range.min !== undefined && value < paramDef.range.min) {
				return { success: false, error: `Value must be >= ${paramDef.range.min}` };
			}
			if (paramDef.range.max !== undefined && value > paramDef.range.max) {
				return { success: false, error: `Value must be <= ${paramDef.range.max}` };
			}
		}

		// Enum validation
		if (paramDef.enumValues && !paramDef.enumValues.includes(value)) {
			return { success: false, error: `Value must be one of: ${paramDef.enumValues.join(', ')}` };
		}

		return { success: true };
	}

	private getPropertyValue(state: Record<string, any>, property: string): any {
		// Support dot notation for nested properties
		const parts = property.split('.');
		let value = state;

		for (const part of parts) {
			if (value && typeof value === 'object' && part in value) {
				value = value[part];
			} else {
				return undefined;
			}
		}

		return value;
	}

	private compareValues(currentValue: any, operator: ComparisonOperator, expectedValue: any): boolean {
		switch (operator) {
			case 'equals':
				return currentValue === expectedValue;
			case 'not_equals':
				return currentValue !== expectedValue;
			case 'greater_than':
				return currentValue > expectedValue;
			case 'less_than':
				return currentValue < expectedValue;
			case 'greater_equal':
				return currentValue >= expectedValue;
			case 'less_equal':
				return currentValue <= expectedValue;
			case 'contains':
				return String(currentValue).includes(String(expectedValue));
			case 'starts_with':
				return String(currentValue).startsWith(String(expectedValue));
			case 'ends_with':
				return String(currentValue).endsWith(String(expectedValue));
			default:
				throw new Error(`Unsupported comparison operator: ${operator}`);
		}
	}
}

// Export singleton instance
export const sceneConditionEngine = SceneConditionEngine.getInstance();
